/**
 * Sprach_Cache der I18n_Infrastruktur.
 *
 * Kapselt den Lese-/Schreibzugriff auf die zuletzt bekannte Aktive_Sprache im
 * Browser-`localStorage`. Der Sprach_Cache ist nicht die Quelle der Wahrheit,
 * sondern ein schneller Start-Cache/Fallback (Design-Komponente "Sprach_Cache").
 *
 * Alle `localStorage`-Zugriffe sind in `try/catch` gekapselt, damit die App auch
 * im Private-Mode oder bei fehlendem `localStorage` (z. B. SSR) nicht abbricht:
 * Lesefehler liefern `null`, Schreibfehler sind ein no-op.
 */

import { SUPPORTED_LANGUAGES, type Language } from "./catalog";

const STORAGE_KEY = "schoolescort.language";

/** Prüft, ob ein Rohwert eine Unterstützte_Sprache benennt (Req 7.6). */
function isSupportedLanguage(value: unknown): value is Language {
  return (
    typeof value === "string" &&
    SUPPORTED_LANGUAGES.includes(value as Language)
  );
}

/**
 * Liest den Sprach_Cache aus dem `localStorage`.
 *
 * Gibt den Wert nur zurück, wenn er eine Unterstützte_Sprache ist; andernfalls
 * (fehlender Eintrag, ungültiger Wert oder `localStorage`-Fehler) `null` (Req 7.6).
 */
export function readLanguage(): Language | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return isSupportedLanguage(stored) ? stored : null;
  } catch {
    return null;
  }
}

/**
 * Schreibt die Aktive_Sprache in den Sprach_Cache (Req 7.5).
 *
 * Fehler beim `localStorage`-Zugriff werden geschluckt (no-op).
 */
export function writeLanguage(lang: Language): void {
  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    // no-op: Sprach_Cache ist nur ein Fallback, kein harter Fehler.
  }
}
